import { Component, OnInit, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { CounselorNav } from '../../counselor-nav/counselor-nav';
import { RoleService, Role, Permission, SchoolUser } from '../../../../../services/role.service';
import { forkJoin, take } from 'rxjs';

@Component({
  selector: 'app-manage-user',
  standalone: true,
  imports: [CounselorNav, CommonModule, FormsModule],
  templateUrl: './manage-user.html',
  styleUrl: './manage-user.css',
})
export class ManageUser implements OnInit {
  private roleService = inject(RoleService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private cdr = inject(ChangeDetectorRef);

  user: SchoolUser | null = null;
  roles: Role[] = [];
  permissions: Permission[] = [];
  loading = true;
  saving = false;
  errorMessage: string | null = null;
  saveError: string | null = null;

  /** Selected role IDs (working copy) */
  selectedRoleIds = new Set<number>();
  /** Selected direct permission IDs (working copy) */
  selectedPermissionIds = new Set<number>();

  /** Original values for cancel/dirty detection */
  private originalRoleIds = new Set<number>();
  private originalPermissionIds = new Set<number>();

  /** Search/filter for permissions */
  permissionSearch = '';
  permissionCategoryFilter = '';

  ngOnInit(): void {
    const userId = Number(this.route.snapshot.paramMap.get('id'));
    if (!userId) {
      this.router.navigate(['/counselors/users']);
      return;
    }
    this.loadData(userId);
  }

  private loadData(userId: number): void {
    this.loading = true;

    forkJoin({
      user: this.roleService.getUserWithDetails(userId).pipe(take(1)),
      roles: this.roleService.getRoles().pipe(take(1)),
      permissions: this.roleService.getPermissions().pipe(take(1)),
    }).subscribe({
      next: ({ user, roles, permissions }) => {
        this.user = user;
        this.roles = roles;
        this.permissions = permissions;

        // Initialize selections from user data
        this.selectedRoleIds = new Set(user.Roles.map((r) => r.id));
        this.selectedPermissionIds = new Set((user.Permissions ?? []).map((p) => p.id));

        // Store originals for cancel
        this.originalRoleIds = new Set(this.selectedRoleIds);
        this.originalPermissionIds = new Set(this.selectedPermissionIds);

        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Failed to load user details:', err);
        this.errorMessage = err?.error?.message || err?.message || 'Failed to load user details.';
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  get displayName(): string {
    if (!this.user) return '';
    const first = this.user.preferredName || this.user.first_name || '';
    return `${first} ${this.user.last_name || ''}`.trim();
  }

  get isDirty(): boolean {
    if (this.selectedRoleIds.size !== this.originalRoleIds.size) return true;
    if (this.selectedPermissionIds.size !== this.originalPermissionIds.size) return true;
    for (const id of this.selectedRoleIds) {
      if (!this.originalRoleIds.has(id)) return true;
    }
    for (const id of this.selectedPermissionIds) {
      if (!this.originalPermissionIds.has(id)) return true;
    }
    return false;
  }

  get permissionCategories(): string[] {
    const cats = new Set(this.permissions.map((p) => p.category));
    return Array.from(cats).sort();
  }

  get filteredPermissions(): Permission[] {
    let result = this.permissions;

    if (this.permissionCategoryFilter) {
      result = result.filter((p) => p.category === this.permissionCategoryFilter);
    }

    if (this.permissionSearch.trim()) {
      const term = this.permissionSearch.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(term) ||
          p.key.toLowerCase().includes(term) ||
          (p.description && p.description.toLowerCase().includes(term)),
      );
    }

    return result;
  }

  /** The server only lets users assign or remove roles whose permissions they all hold. */
  isRoleLocked(role: Role): boolean {
    return role.can_assign === false;
  }

  /** The server only lets users grant or revoke permissions they hold themselves. */
  isPermissionLocked(permission: Permission): boolean {
    return permission.assignable === false;
  }

  toggleRole(roleId: number): void {
    const role = this.roles.find((r) => r.id === roleId);
    if (role && this.isRoleLocked(role)) return;

    if (this.selectedRoleIds.has(roleId)) {
      this.selectedRoleIds.delete(roleId);
    } else {
      this.selectedRoleIds.add(roleId);
    }
  }

  hasRole(roleId: number): boolean {
    return this.selectedRoleIds.has(roleId);
  }

  togglePermission(permissionId: number): void {
    const permission = this.permissions.find((p) => p.id === permissionId);
    if (permission && this.isPermissionLocked(permission)) return;

    if (this.selectedPermissionIds.has(permissionId)) {
      this.selectedPermissionIds.delete(permissionId);
    } else {
      this.selectedPermissionIds.add(permissionId);
    }
  }

  hasPermission(permissionId: number): boolean {
    return this.selectedPermissionIds.has(permissionId);
  }

  save(): void {
    if (!this.user || this.saving) return;

    this.saving = true;
    this.saveError = null;
    this.roleService
      .updateUserRolesAndPermissions(
        this.user.id,
        Array.from(this.selectedRoleIds),
        Array.from(this.selectedPermissionIds),
      )
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.saving = false;
          this.router.navigate(['/counselors/users']);
        },
        error: (err) => {
          console.error('Failed to save user roles/permissions:', err);
          this.saveError = err?.error?.message || err?.message || 'Failed to save changes.';
          this.saving = false;
          this.cdr.detectChanges();
        },
      });
  }

  cancel(): void {
    this.router.navigate(['/counselors/users']);
  }
}
